/**
 * MyPayslipsPage — Liquidaciones del colaborador.
 *
 * Usada en AMBOS contextos:
 *   - Caso A: /colaboradores/payslips (PortalLayout, portalApiClient)
 *   - Caso B: /portal/payslips (AppLayout, apiClient ERP)
 *
 * El hook usePortalPayslips detecta el contexto automáticamente por pathname.
 */
import { FileText } from 'lucide-react'
import { usePortalPayslips } from '../hooks/useEmployeePortal'
import { EmptyState } from '@/components/ds/EmptyState'
import { StatusBadge, PAYSLIP_STATUS } from '@/components/ds/StatusBadge'
import { DataTable } from '@/components/ds/DataTable'
import { Monto } from '@/components/ds/Monto'
import { useMoney } from '@/lib/locale'
import { VerLiquidacion } from '../components/DescargarLiquidacion'
import { PaginaPortal } from '../components/PaginaPortal'

export default function MyPayslipsPage() {
  const { data, isLoading, error } = usePortalPayslips()
  // La moneda sale del tenant: antes estaba el peso chileno escrito a mano.
  const money = useMoney()
  const payslips = data?.results ?? []

  return (
    <PaginaPortal titulo="Liquidaciones" bajada="Historial de liquidaciones de sueldo aprobadas y pagadas.">
      {error ? (
        <EmptyState variant="error" title="No se pudieron cargar las liquidaciones" />
      ) : (
        <DataTable
          rows={payslips}
          rowKey={(p: any) => p.id}
          loading={isLoading}
          minWidth={760}
          caption="Liquidaciones de sueldo del colaborador"
          empty={
            <EmptyState
              icon={<FileText size={28} aria-hidden="true" />}
              title="Sin liquidaciones disponibles"
              description="Todavía no hay liquidaciones aprobadas."
            />
          }
          columns={[
            {
              key: 'period_label', header: 'Período', sortable: true,
              render: (p: any) => <span className="font-medium text-content">{p.period_label}</span>,
            },
            {
              key: 'gross_salary', header: 'Sueldo bruto', numeric: true, sortable: true,
              sortValue: (p: any) => Number(p.gross_salary),
              render: (p: any) => <Monto>{money(p.gross_salary)}</Monto>,
            },
            {
              key: 'total_deductions', header: 'Descuentos', numeric: true, sortable: true,
              sortValue: (p: any) => Number(p.total_deductions),
              render: (p: any) => <Monto>{money(p.total_deductions)}</Monto>,
            },
            {
              key: 'net_salary', header: 'Líquido', numeric: true, sortable: true,
              sortValue: (p: any) => Number(p.net_salary),
              render: (p: any) => <Monto className="font-semibold">{money(p.net_salary)}</Monto>,
            },
            {
              key: 'status', header: 'Estado', width: 120,
              render: (p: any) => <StatusBadge status={p.status} catalog={PAYSLIP_STATUS} />,
            },
            {
              key: 'pdf', header: <span className="sr-only">PDF</span>, width: 90,
              render: (p: any) => <VerLiquidacion payslipId={p.id} periodLabel={p.period_label} />,
            },
          ]}
        />
      )}
    </PaginaPortal>
  )
}
